/**
 * Set/get/remove items from local storage, fallback to cookies if local storage is not available.
 *
 * Usage:
 *   var storage = new Storage;
 *
 *   storage.set(key, value);
 *   storage.get(key);
 *   storage.remove(key);
 *
 */
class Storage {
	useLocalStorage = false;

	options = {
		preferLocalStorage: true,
		cookieExpirationDays: 120,
		cookiePath: '/',
	};

	constructor(customOptions) {
		if (customOptions) {
			Object.assign(this.options, customOptions);
		}

		const checkSupport = function () {
			try {
				const storage = window['localStorage'];
				const test = '__test__';

				// Set and remove an item from local storage to confirm that it is enabled.
				// Safari in private browsing mode may report that localStorage is available
				// when it is not and throw a QuotaExceededError later.
				storage.setItem(test, test);
				storage.removeItem(test);

				return true;
			} catch (e) {
				return false;
			}
		};

		if (this.options.preferLocalStorage && checkSupport()) {
			this.useLocalStorage = true;
		}
	}

	/**
	 * Read cookie.
	 *
	 * @param {String} key
	 * @return {String|null}
	 */
	readCookie(key) {
		const keyEquals = key + '=';
		const cookieArray = document.cookie.split(';');

		for (let i = 0, cookieCount = cookieArray.length; i < cookieCount; i++) {
			let cookie = cookieArray[i];

			// Remove any space preceeding the cookie value
			while (cookie.charAt(0) === ' ') {
				cookie = cookie.substring(1, cookie.length);
			}

			if (cookie.indexOf(keyEquals) === 0) {
				return cookie.substring(keyEquals.length, cookie.length);
			}
		}

		return null;
	}

	/**
	 * Set item in local storage, fallback to cookies.
	 *
	 * @param {String} key
	 * @param {String} value
	 */
	set(key, value) {
		if (typeof value !== 'undefined' && typeof value === 'string') {
			if (this.useLocalStorage) {
				localStorage.setItem(key, value);
			} else {
				this.createCookie(key, value, this.options.cookieExpirationDays);
			}
		}
	}

	/**
	 * Remove cookie.
	 *
	 * @param {String} key
	 */
	removeCookie(key) {
		this.createCookie(key, '', -1);
	}

	/**
	 * Create cookie.
	 *
	 * @param {String} key
	 * @param {String} value
	 * @param {Number} expire The number of days until the cookie expires
	 * @param {String} domain
	 */
	createCookie(key, value, expire, domain = null) {
		const date = new Date();

		date.setTime(date.getTime() + expire * 24 * 60 * 60 * 1000);

		let cookie = key + '=' + value + '; expires=' + date.toGMTString() + '; path=' + this.options.cookiePath;
		if (domain !== null && typeof domain === 'string' && domain.length > 3) {
			cookie += '; domain=' + domain;
		}

		document.cookie = cookie;
	}

	/**
	 * Get item from local storage, fallback to cookies.
	 *
	 * @param {String} key
	 * @return {String|null}
	 */
	get(key) {
		return this.useLocalStorage ? localStorage.getItem(key) : this.readCookie(key);
	}

	/**
	 * Remove item from local storage, fallback to cookies.
	 *
	 * @param {String} key
	 */
	remove(key) {
		if (this.useLocalStorage) {
			localStorage.removeItem(key);
		} else {
			this.removeCookie(key);
		}
	}
}

export default Storage;
